import { type KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { SceneBack } from '@/shared/components/SceneBack';
import {
  findCharacter,
  getSuggestions,
  portraitFor,
} from '../data/characters';
import './WatchTopic.css';

/**
 * Watch flow — step 2 of 2: choose the topic.
 *
 * The user arrives here from /arena/watch with a chosen pair (in router
 * state). They get a freeform input plus a list of pair-aware suggestions
 * generated by `getSuggestions(left, right)`. Clicking a suggestion fills
 * the input; the user can then tweak before begin. Begin → /arena/watch/debate.
 *
 * If someone deep-links here without picks, we bounce back to /arena/watch.
 */

interface TopicLocationState {
  leftId?: string;
  rightId?: string;
}

/**
 * Persist the picked pair across hard-refresh and across the back-from-debate
 * flow (Return on the debate page lands here, and SceneBack doesn't carry
 * router state). The debate page writes its own superset of this under
 * `arena.watch.lastDebate`; we honor either.
 */
const PAIR_SESSION_KEY = 'arena.watch.pair';
const DEBATE_SESSION_KEY = 'arena.watch.lastDebate';

function readPersistedPair(): TopicLocationState {
  try {
    const pair = sessionStorage.getItem(PAIR_SESSION_KEY);
    if (pair) {
      const parsed = JSON.parse(pair) as TopicLocationState;
      if (parsed.leftId && parsed.rightId) return parsed;
    }
    const debate = sessionStorage.getItem(DEBATE_SESSION_KEY);
    if (debate) {
      const parsed = JSON.parse(debate) as TopicLocationState;
      if (parsed.leftId && parsed.rightId) return parsed;
    }
  } catch {
    // ignore quota / privacy-mode errors
  }
  return {};
}

export function WatchTopic() {
  const location = useLocation();
  const navigate = useNavigate();

  const { leftId, rightId } = useMemo<TopicLocationState>(() => {
    const fromState = (location.state ?? {}) as TopicLocationState;
    if (fromState.leftId && fromState.rightId) {
      try {
        sessionStorage.setItem(PAIR_SESSION_KEY, JSON.stringify(fromState));
      } catch {
        // ignore
      }
      return fromState;
    }
    return readPersistedPair();
  }, [location.state]);

  const left = useMemo(() => findCharacter(leftId ?? null), [leftId]);
  const right = useMemo(() => findCharacter(rightId ?? null), [rightId]);

  const [topic, setTopic] = useState('');

  const suggestions = useMemo(
    () => getSuggestions(leftId ?? null, rightId ?? null, 6),
    [leftId, rightId],
  );

  useEffect(() => {
    document.title = left && right
      ? `${left.name} vs ${right.name} — The Arena`
      : 'The Stands — The Arena';
  }, [left, right]);

  // Direct nav / stale state → back to step 1.
  if (!leftId || !rightId || !left || !right) {
    return <Navigate to="/arena/watch" replace />;
  }

  const isReady = topic.trim().length > 0;

  const handleBegin = () => {
    if (!isReady) return;
    navigate('/arena/watch/debate', {
      state: { leftId, rightId, topic: topic.trim() },
    });
  };

  const handleSuggestionPick = (prompt: string) => {
    setTopic(prompt);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isReady) {
      e.preventDefault();
      handleBegin();
    }
  };

  return (
    <main className="scene scene--setup">
      <div
        className="scene__image scene__image--watch"
        role="img"
        aria-label="A roaring crowd packed into the colosseum stands"
      />
      <div className="scene__vignette" />

      <SceneBack />

      <div className="scene__content scene__content--setup">
        <p className="scene__eyebrow">— The Stands —</p>
        <h1 className="scene__title scene__title--setup">
          What Will They Argue?
        </h1>

        <div className="topic-pair" aria-label="Matchup">
          <div className="topic-pair__side topic-pair__side--left">
            <img
              className="topic-pair__portrait"
              src={portraitFor(left.id)}
              alt=""
              loading="eager"
            />
            <em className="topic-pair__name">{left.name}</em>
          </div>
          <span className="topic-pair__vs" aria-hidden="true">
            vs
          </span>
          <div className="topic-pair__side topic-pair__side--right">
            <img
              className="topic-pair__portrait"
              src={portraitFor(right.id)}
              alt=""
              loading="eager"
            />
            <em className="topic-pair__name">{right.name}</em>
          </div>
        </div>

        <div className="topic-area">
          <input
            type="text"
            className="topic-area__input"
            placeholder="Enter a topic…"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            autoFocus
          />
        </div>

        {suggestions.length > 0 && (
          <div className="topic-suggestions" aria-label="Suggested topics">
            <p className="topic-suggestions__label">— Or pick a topic —</p>
            <ul className="topic-suggestions__list">
              {suggestions.map((prompt) => {
                const isActive = topic.trim() === prompt;
                return (
                  <li key={prompt} className="topic-suggestions__item">
                    <button
                      type="button"
                      className={`topic-suggestions__btn${isActive ? ' is-active' : ''}`}
                      onClick={() => handleSuggestionPick(prompt)}
                    >
                      {prompt}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <button
          type="button"
          className="watch-begin"
          disabled={!isReady}
          onClick={handleBegin}
        >
          <span className="watch-begin__label">Begin the Debate</span>
          <span aria-hidden="true" className="watch-begin__arrow">
            →
          </span>
        </button>
      </div>
    </main>
  );
}
